import type { Incident, IncidentGroup } from '../../services/incidentsApi';

type IncidentPatternLike = IncidentGroup | Incident | Record<string, any> | null | undefined;

const RECURRING_PATTERN_STATES = new Set([
  'recurring_hotspot',
  'emerging_pattern',
  'recurring',
  'hotspot',
]);

function sourceRecord(value: IncidentPatternLike): Record<string, any> | null {
  if (!value || typeof value !== 'object') return null;
  const raw = (value as any).raw;
  if (raw && typeof raw === 'object') return raw as Record<string, any>;
  return value as Record<string, any>;
}

function toCount(value: unknown): number {
  const count = Number(value ?? 0);
  return Number.isFinite(count) ? Math.max(0, count) : 0;
}

export function getIncidentPatternSelectedSignalCount(
  record: IncidentPatternLike,
): number {
  const source = sourceRecord(record);
  return Math.max(
    0,
    toCount(
      source?.pattern_source?.selected_window_alert_count
      ?? source?.selected_window_alert_count
      ?? source?.related_occurrence_count
      ?? source?.related_alert_count
      ?? source?.recurrence_signal
      ?? source?.recurrence_signal_total
      ?? source?.alert_count
      ?? 0,
    ),
  );
}

export function getIncidentPatternSeriesCount(
  record: IncidentPatternLike,
): number {
  const source = sourceRecord(record);
  return Math.max(
    0,
    toCount(
      source?.incident_series_count
      ?? source?.series_count
      ?? source?.related_incident_count
      ?? source?.related_alert_count
      ?? source?.alert_count
      ?? 0,
    ),
  );
}

export function hasIncidentPatternMetadata(record: IncidentPatternLike): boolean {
  const source = sourceRecord(record);
  return Boolean(
    source?.pattern_source
    || source?.risk_pattern
    || String(source?.pattern_source?.pattern_label || '').trim()
    || String(source?.pattern_source?.pattern_summary || '').trim()
    || String(source?.risk_pattern?.label || '').trim()
    || String(source?.risk_pattern?.summary || '').trim(),
  );
}

function hasStrongGroupedOccurrenceEvidence(...records: Array<IncidentPatternLike>): boolean {
  return records.some((record) => {
    const source = sourceRecord(record);
    if (!source) return false;
    return (
      getIncidentPatternSelectedSignalCount(record) > 1
      || getIncidentPatternSeriesCount(record) > 1
      || toCount(source.alert_count) > 1
      || toCount(source.related_alert_count) > 1
      || toCount(source.related_occurrence_count) > 1
    );
  });
}

function hasRecurringPatternState(record: IncidentPatternLike): boolean {
  const source = sourceRecord(record);
  if (!source) return false;
  const state = String(
    source.pattern_source?.pattern_state
    || source.risk_pattern?.state
    || '',
  )
    .trim()
    .toLowerCase();
  if (state && RECURRING_PATTERN_STATES.has(state)) return true;
  if (state.includes('recurring') || state.includes('hotspot')) return true;
  const tag = String(source.recurrence_tag || source.risk_pattern?.label || '').toLowerCase();
  return tag.includes('recurring') || tag.includes('hotspot');
}

function hasExplicitGroupedPatternState(record: IncidentPatternLike): boolean {
  const source = sourceRecord(record);
  if (!source) return false;
  const state = String(source.pattern_source?.pattern_state || '').trim().toLowerCase();
  return Boolean(state && state !== 'single' && state !== 'none');
}

/**
 * Grouped Pattern Review requires strong evidence of a multi-occurrence pattern.
 * Incidental dimensions (policy_id, zone_id, camera_id) alone do not trigger grouped mode.
 */
export function isGroupedIncident(
  incident: IncidentPatternLike,
  groupContext?: IncidentGroup | null,
): boolean {
  if (groupContext && typeof groupContext === 'object') {
    if (String(groupContext.group_key || '').trim()) return true;
    if (Boolean(groupContext.is_grouped)) return true;
  }

  const incidentSource = sourceRecord(incident);
  const groupSource = sourceRecord(groupContext);

  if (hasStrongGroupedOccurrenceEvidence(groupSource, incidentSource)) {
    return true;
  }

  const metaCarrier = groupSource || incidentSource;
  if (metaCarrier && hasIncidentPatternMetadata(metaCarrier)) {
    if (
      hasStrongGroupedOccurrenceEvidence(metaCarrier)
      && (hasRecurringPatternState(metaCarrier) || hasExplicitGroupedPatternState(metaCarrier))
    ) {
      return true;
    }
  }

  return false;
}

export function getIncidentGroupPatternState(group: IncidentGroup | null | undefined): string {
  return String(group?.pattern_source?.pattern_state || group?.risk_pattern?.state || '').trim().toLowerCase();
}

export function getIncidentGroupPatternLabel(group: IncidentGroup | null | undefined): string | undefined {
  return String(
    group?.pattern_source?.pattern_label
    || group?.risk_pattern?.label
    || group?.recurrence_tag
    || '',
  ).trim() || undefined;
}

export function getIncidentGroupPatternSummary(group: IncidentGroup | null | undefined): string | undefined {
  return String(
    group?.pattern_source?.pattern_summary
    || group?.risk_pattern?.summary
    || '',
  ).trim() || undefined;
}

export function getIncidentGroupSelectedSignalCount(group: IncidentGroup | null | undefined): number {
  return getIncidentPatternSelectedSignalCount(group);
}

export function getIncidentGroupLookbackSignalCount(group: IncidentGroup | null | undefined): number {
  return Math.max(
    0,
    Number(
      group?.pattern_source?.lookback_signal_count
      ?? group?.recent_alert_count
      ?? group?.alert_count
      ?? 0,
    ),
  );
}

export function isIncidentGroupRecurring(group: IncidentGroup | null | undefined): boolean {
  const state = getIncidentGroupPatternState(group);
  if (state === 'recurring_hotspot' || state === 'emerging_pattern') return true;
  return String(group?.recurrence_tag || '').toLowerCase().includes('recurring');
}

export function isIncidentGroupRecurringHotspot(group: IncidentGroup | null | undefined): boolean {
  const state = getIncidentGroupPatternState(group);
  if (state === 'recurring_hotspot') return true;
  return String(group?.recurrence_tag || '').toLowerCase().includes('recurring hotspot');
}

/** Short operator-facing recurrence chip for grouped review headers. */
export function getGroupedReviewRecurrenceStatusLabel(
  record: IncidentPatternLike,
): string {
  const source = sourceRecord(record);
  if (!source) return '';
  if (isIncidentGroupRecurringHotspot(record as IncidentGroup)) return 'Recurring risk area';
  const label = getIncidentGroupPatternLabel(record as IncidentGroup)
    || String(source.recurrence_tag || '').trim();
  if (!label) return '';
  if (isIncidentGroupRecurring(record as IncidentGroup)) return label;
  const lower = label.toLowerCase();
  if (lower.includes('recurring')) return label;
  return '';
}

/** Representative incident id for grouped evidence review / close-resolve flows. */
export function resolveRepresentativeIncidentIdForReview(
  incident: IncidentPatternLike,
  groupContext?: IncidentGroup | null,
): string {
  const incidentId = String((sourceRecord(incident) as any)?.incident_id || '').trim();
  if (incidentId) return incidentId;
  const group = sourceRecord(groupContext) as Record<string, any> | null;
  if (!group) return '';
  return String(
    group.linked_detailed_record_id
    || group.pattern_source?.representative_incident_id
    || group.incident_id
    || '',
  ).trim();
}
